import * as XLSX from "xlsx";
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { MainLayout } from "../layout/MainLayout";
import { AlertCircle, Loader2 } from "lucide-react";
import Card from "../common/Card";
import SectionHeader from "../common/SectionHeader";
import FileUploadZone from "../uploads/FileUploadZone";
import FileTable from "../uploads/FileTable";
import PrimaryButton from "../ui/PrimaryButton";
import Toast from "../common/Toast";
import usePVPanelScenarios from "../hooks/usePVPanelScenarios";

const LoadingScreen = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
  </div>
);

const ErrorScreen = ({ message }) => (
  <div className="flex flex-col items-center justify-center min-h-screen">
    <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
    <p className="text-red-600">Error: {message}</p>
  </div>
);
const EmptyState = ({ message }) => (
  <div className="text-center py-12 bg-gray-50 rounded-lg">
    <p className="text-gray-600">{message || "No data available."}</p>
  </div>
);

const PVPanelSetup = () => {
  // Get project ID from URL parameters
  const { projectId } = useParams();

  const [newScenario, setNewScenario] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [toast, setToast] = useState(null);

  const {
    scenarios,
    loading,
    isProcessing,
    error,
    selectedScenarios,
    handleSelectScenario,
    handleSelectAllScenarios,
    deleteScenarios,
    uploadScenario,
  } = usePVPanelScenarios(projectId);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };

  const handleFilesSelect = (files) => {
    if (files.length === 0) return;
    setSelectedFile(files[0]);
  };

  const handleDeleteScenarios = async () => {
    if (selectedScenarios.length === 0) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedScenarios.length} scenario(s)?`
    );

    if (confirmDelete) {
      const result = await deleteScenarios();
      showToast(result.message, result.success ? "success" : "error");
    }
  };

  const handleUploadScenario = async () => {
    const result = await uploadScenario(newScenario, selectedFile);
    showToast(result.message, result.success ? "success" : "error");

    if (result.success) {
      setNewScenario("");
      setSelectedFile(null);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <LoadingScreen />
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <ErrorScreen message={error} />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card
            title={`PV Panel Configuration - Project ${projectId}`}
            subtitle="Upload and manage your PV panel scenarios"
          >
            <div className="space-y-6 p-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6">
                  <SectionHeader
                    title="Manage Scenarios"
                    action={
                      selectedScenarios.length > 0 && (
                        <PrimaryButton
                          onClick={handleDeleteScenarios}
                          isLoading={isProcessing}
                          loadingText="Deleting..."
                          className="bg-red-600 hover:bg-red-700"
                          width="auto"
                        >
                          Delete Selected ({selectedScenarios.length})
                        </PrimaryButton>
                      )
                    }
                  />

                  {scenarios.length > 0 ? (
                    <FileTable
                      files={scenarios.map((scenario) => ({
                        id: scenario,
                        name: scenario,
                        type: "PV Scenario",
                        _created_at: null,
                        _updated_at: null,
                      }))}
                      selectedFiles={selectedScenarios}
                      onSelectFile={handleSelectScenario}
                      onSelectAll={handleSelectAllScenarios}
                    />
                  ) : (
                    <EmptyState message="No scenarios available. Upload a new scenario to get started." />
                  )}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6">
                  <SectionHeader title="Upload New Scenario" />

                  <div className="space-y-4">
                    <div>
                      <label
                        htmlFor="scenario-name"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Scenario Name
                      </label>
                      <input
                        id="scenario-name"
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm 
                          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter scenario name"
                        value={newScenario}
                        onChange={(e) => setNewScenario(e.target.value)}
                      />
                    </div>

                    <FileUploadZone
                      onFilesSelect={handleFilesSelect}
                      disabled={isProcessing}
                      accept=".xlsx,.xls"
                      acceptedTypes={["XLSX", "XLS"]}
                    />

                    <PrimaryButton
                      onClick={handleUploadScenario}
                      isLoading={isProcessing}
                      loadingText="Processing..."
                      disabled={!selectedFile || !newScenario}
                    >
                      Upload Scenario
                    </PrimaryButton>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default PVPanelSetup;
